import { ChangeDetectionStrategy, Component, signal, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IrisDMSFolder, IrisDMSFolderI } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisDmsSettingsSandbox } from '@iris/common/modules/dms/sandbox/dms-settings.sandbox';
import { IrisTagType } from '@iris/common/models/IrisTag';
import { IrisDmsPermissionsSandbox } from '@iris/common/modules/dms/sandbox/dms-permissions.sandbox';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisTranslatedField } from '@iris/common/models';
import { TranslatePipe } from '@ngx-translate/core';
import { StrakitButtonComponent, StrakitSelectComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { IrisFieldTranslateModule } from '@iris/common/modules/fields/field-translate/field-translate.module';
import { AsyncPipe } from '@angular/common';
import { NgOptionTemplateDirective } from '@ngx-strakit/ng-select';
import { StrakitColorComponent } from '@ngx-strakit/cdk/controls/color';
import { ColorService } from '@ngx-strakit/cdk/services';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

@Component({
  selector: 'folder-edit-modal',
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TranslatePipe,
    NgOptionTemplateDirective,
    IrisFieldTranslateModule,
    SkModalContentComponent,
    StrakitTextComponent,
    StrakitSelectComponent,
    StrakitColorComponent,
    ConfirmByKeypressDirective,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './folder-edit-modal.component.html',
  styleUrls: ['./folder-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsFolderEditModalComponent extends SkModalBase<IrisDmsFolderEditModalComponent, void> {
  private readonly fb = inject(FormBuilder);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsSettingsSandbox = inject(IrisDmsSettingsSandbox);
  private readonly dmsPermissionsSandbox = inject(IrisDmsPermissionsSandbox);
  private readonly folderEditColor = inject(ColorService);
  readonly data = inject<{
    folder: IrisDMSFolderI;
  }>(MAT_DIALOG_DATA);

  readonly properties = toSignal(this.dmsSettingsSandbox.folderProperties$);

  readonly isFlagSymbolActive = toSignal(this.dmsSettingsSandbox.isPropertyActive$(IrisTagType.FlagSymbols));
  readonly folderInitialColor = signal('');

  private folder = new IrisDMSFolder();

  readonly editFolderForm: FormGroup = this.fb.group({
    name: new FormControl('', [Validators.required]),
    comment: new FormControl(),
    customIconClass: new FormControl(),
    color: new FormControl(this.folderEditColor.colors.$warning),
  });

  @ViewChild(StrakitColorComponent, { static: true }) fieldColor: StrakitColorComponent;

  constructor() {
    super();
    this.initFolderInfo(this.data.folder);
  }

  get translations(): IrisTranslatedField {
    return this.folder?.translations?.name;
  }

  get isAdmin(): boolean {
    return this.dmsPermissionsSandbox.isAdmin;
  }

  get editFormValue(): IrisDMSFolderI {
    return this.editFolderForm.value as IrisDMSFolderI;
  }

  initFolderInfo(folder: IrisDMSFolderI): void {
    this.folder = new IrisDMSFolder(folder);
    this.folderInitialColor.set(this.folder.color);
    this.editFolderForm.patchValue(this.folder);
  }

  onSubmit(): void {
    this.setCustomIconClass();
    const folder = {
      ...this.editFormValue,
      id: this.folder.id,
      translations: this.folder.translations,
    };
    this.dmsFoldersSandbox.updateFolder(folder);
    this.closeWithoutResult();
  }

  folderNameTranslationsChanged(translations: IrisTranslatedField): void {
    const folderTranslations = {
      ...this.folder.translations,
      name: translations,
    };
    this.folder.translations = folderTranslations;
  }

  private setCustomIconClass(): void {
    if (!this.isFlagSymbolActive() && this.editFormValue.color !== this.folderInitialColor()) {
      this.editFolderForm.get('customIconClass').setValue(null);
    }
  }
}
